/**
 * Offer Card Component
 * Displays tutor offers for assignments
 */

import React from 'react';
import { DollarSign, Clock, Check, CheckCircle } from 'lucide-react';
import { formatDistance } from 'date-fns';
import MessageButton from '../../../features/messaging/components/MessageButton'; // Updated import

const OfferCard = ({ offer, isOwner, onAccept, isLoading }) => {
  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return 'Unknown time';
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  };
  
  // Get status badge props
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-800',
          text: 'Pending'
        };
      case 'accepted':
        return {
          color: 'bg-green-100 text-green-800',
          text: 'Accepted'
        };
      case 'rejected':
        return {
          color: 'bg-red-100 text-red-800',
          text: 'Rejected'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          text: 'Unknown'
        };
    }
  };
  
  const statusBadge = getStatusBadge(offer.status);
  
  return (
    <div className={`bg-white rounded-lg shadow-sm border overflow-hidden transition ${
      offer.status === 'accepted' 
        ? 'border-green-300 ring-1 ring-green-300' 
        : 'border-gray-200'
    }`}>
      {/* Card Header with Tutor Info */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
            {offer.tutorProfile?.photos && offer.tutorProfile.photos.length > 0 ? (
              <img 
                src={offer.tutorProfile.photos[0]} 
                alt={offer.tutorProfile?.basicInfo?.name || 'Tutor'} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500 font-medium">
                {offer.tutorProfile?.basicInfo?.name?.charAt(0) || 'T'}
              </div>
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-800">
              {offer.tutorProfile?.basicInfo?.name || 'Anonymous Tutor'}
              {offer.status === 'accepted' && (
                <span className="ml-2 inline-flex items-center text-green-600 text-xs">
                  <CheckCircle size={12} className="mr-1" />
                  Selected Tutor
                </span>
              )}
            </h3>
            <p className="text-xs text-gray-500">
              {offer.tutorProfile?.academic?.university || 'University not specified'}
            </p>
          </div>
        </div>
        
        <span className={`text-xs px-2 py-1 rounded-full ${statusBadge.color}`}>
          {statusBadge.text}
        </span>
      </div>
      
      {/* Card Body */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Price */}
          <div className="flex items-center">
            <DollarSign size={16} className="text-gray-500 mr-2" />
            <div>
              <p className="text-xs text-gray-500">Price</p>
              <p className="font-medium text-green-600">R{offer.price}</p>
            </div>
          </div>
          
          {/* Time Estimate */}
          <div className="flex items-center">
            <Clock size={16} className="text-gray-500 mr-2" />
            <div>
              <p className="text-xs text-gray-500">Time Estimate</p>
              <p className="font-medium">{offer.timeEstimate} hours</p>
            </div>
          </div>
        </div>
        
        {/* Message */}
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1">Message:</p>
          <p className="text-sm text-gray-700 whitespace-pre-line">
            {offer.message}
          </p>
        </div>
        
        {/* Offer Time */}
        <p className="text-xs text-gray-500">
          Offered {formatRelativeTime(offer.createdAt)}
        </p>
      </div>
      
      {/* Card Footer with Actions */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-end space-x-2">
        {isOwner && offer.status === 'pending' ? (
          <>
            <button
              onClick={() => onAccept(offer.id)}
              disabled={isLoading}
              className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded-md transition flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span className="inline-block w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin mr-1"></span>
                  Processing...
                </>
              ) : (
                <>
                  <Check size={14} className="mr-1" />
                  Accept Offer
                </>
              )}
            </button>
            
            <MessageButton
              userId={offer.tutorId}
              variant="secondary"
              className="px-3 py-1.5 rounded-md text-xs"
              showText={true}
            />
          </>
        ) : (
          <MessageButton
            userId={isOwner ? offer.tutorId : offer.ownerId}
            variant="primary"
            className="px-3 py-1.5 rounded-md text-xs"
            showText={true}
          />
        )}
      </div>
    </div>
  );
};

export default OfferCard;